import { App } from "@capacitor/app"
import { AppLauncher } from "@capacitor/app-launcher"
import { Clipboard } from "@capacitor/clipboard"
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem"
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics"
import { PushNotifications } from "@capacitor/push-notifications"
import { Share } from "@capacitor/share"
import { StatusBar, Style as StatusBarStyle } from "@capacitor/status-bar"
import { openUrlInNativeBrowser } from "../electronAPIs/shell"
import { currentPlatformKind, nestClientPlatform } from "./detect"
import { normalizeNotificationPermission, parseForegroundNotification, parseNotificationTap } from "./notifications"
import type {
    AppLifecycleState,
    BackButtonPress,
    ForegroundNotification,
    HapticKind,
    NativeMobilePlatform,
    NotificationPermissionState,
    NotificationTap,
    PlatformAdapter,
    PlatformCapabilities,
    PlatformInfo,
    PlatformKind,
    PlatformListener,
    PlatformResult,
    PushRegistrationEvent,
    ShareContent,
    StatusBarTheme,
    TextFileExport,
} from "./types"

const browserCapabilities: PlatformCapabilities = {
    secureComputerRegistry: "unavailable",
    lifecycle: "available",
    deepLinks: "available",
    haptics: "unavailable",
    nativeNotifications: "unavailable",
    liveStatus: "unavailable",
    externalBrowser: "available",
    share: "runtime-check",
    clipboard: "runtime-check",
    hostDirectoryPicker: "unavailable",
    openHostPath: "unavailable",
    systemBack: "unavailable",
    statusBarTheme: "unavailable",
    fileExport: "available",
}

const electronCapabilities: PlatformCapabilities = {
    ...browserCapabilities,
    externalBrowser: "available",
    hostDirectoryPicker: "available",
    openHostPath: "available",
}

const nativeCapabilities: PlatformCapabilities = {
    secureComputerRegistry: "available",
    lifecycle: "available",
    deepLinks: "available",
    haptics: "available",
    nativeNotifications: "runtime-check",
    liveStatus: "runtime-check",
    externalBrowser: "available",
    share: "available",
    clipboard: "available",
    hostDirectoryPicker: "unavailable",
    openHostPath: "unavailable",
    systemBack: "runtime-check",
    statusBarTheme: "available",
    fileExport: "available",
}

function failure(error: unknown): PlatformResult<never> {
    return { status: "failed", message: error instanceof Error ? error.message : String(error) }
}

function normalizeExternalUrl(value: string): string {
    const parsed = new URL(value)
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:" && parsed.protocol !== "mailto:") {
        throw new Error("External links must use HTTP, HTTPS, or mailto")
    }
    return parsed.toString()
}

function browserLifecycleState(): AppLifecycleState {
    return typeof document === "undefined" || document.visibilityState === "visible" ? "active" : "background"
}

function exportFilename(value: string): string {
    const filename = value.trim()
    if (!filename || filename.length > 160 || filename.includes("/") || filename.includes("\\") || filename.includes("\0")) {
        throw new Error("Export filename is invalid")
    }
    return filename
}

class WebPlatformAdapter implements PlatformAdapter {
    readonly nestClientPlatform
    readonly capabilities

    constructor(readonly kind: Extract<PlatformKind, "browser" | "electron">) {
        this.nestClientPlatform = nestClientPlatform(kind)
        this.capabilities = kind === "electron" ? electronCapabilities : browserCapabilities
    }

    async getInfo(): Promise<PlatformInfo> {
        return { kind: this.kind, native: false }
    }

    async getLifecycleState(): Promise<AppLifecycleState> {
        return browserLifecycleState()
    }

    async addLifecycleListener(listener: (state: AppLifecycleState) => void): Promise<PlatformListener> {
        const handle = () => listener(browserLifecycleState())
        document.addEventListener("visibilitychange", handle)
        return { remove: async () => document.removeEventListener("visibilitychange", handle) }
    }

    async getLaunchUrl(): Promise<string | null> {
        return typeof window === "undefined" ? null : window.location.href
    }

    async addUrlOpenListener(_listener: (url: string) => void): Promise<PlatformListener> {
        return { remove: async () => undefined }
    }

    async getNotificationPermission(): Promise<PlatformResult<NotificationPermissionState>> {
        return { status: "unavailable", reason: "Native push notifications are unavailable on this platform" }
    }

    async requestNotificationPermission(): Promise<PlatformResult<NotificationPermissionState>> {
        return { status: "unavailable", reason: "Native push notifications are unavailable on this platform" }
    }

    async addPushRegistrationListener(_listener: (event: PushRegistrationEvent) => void): Promise<PlatformListener> {
        return { remove: async () => undefined }
    }

    async registerForPushNotifications(): Promise<PlatformResult<void>> {
        return { status: "unavailable", reason: "Native push notifications are unavailable on this platform" }
    }

    async addNotificationTapListener(_listener: (tap: NotificationTap) => void): Promise<PlatformListener> {
        return { remove: async () => undefined }
    }

    async addForegroundNotificationListener(_listener: (notification: ForegroundNotification) => void): Promise<PlatformListener> {
        return { remove: async () => undefined }
    }

    async addBackButtonListener(_listener: (event: BackButtonPress) => void): Promise<PlatformListener> {
        return { remove: async () => undefined }
    }

    async minimizeApp(): Promise<PlatformResult<void>> {
        return { status: "unavailable", reason: "App minimization is unavailable on this platform" }
    }

    async setStatusBarTheme(_theme: StatusBarTheme): Promise<PlatformResult<void>> {
        return { status: "unavailable", reason: "Native status bar theming is unavailable on this platform" }
    }

    async haptic(_kind: HapticKind): Promise<PlatformResult<void>> {
        return { status: "unavailable", reason: "Native haptics are unavailable on this platform" }
    }

    async openExternalUrl(url: string): Promise<PlatformResult<void>> {
        try {
            const externalUrl = normalizeExternalUrl(url)
            if (this.kind === "electron") {
                openUrlInNativeBrowser(externalUrl)
                return { status: "ok", value: undefined }
            }
            const opened = window.open(externalUrl, "_blank", "noopener,noreferrer")
            return opened ? { status: "ok", value: undefined } : { status: "denied", reason: "The browser blocked the new window" }
        } catch (error) {
            return failure(error)
        }
    }

    async share(content: ShareContent): Promise<PlatformResult<void>> {
        if (!navigator.share) return { status: "unavailable", reason: "The Web Share API is unavailable" }
        try {
            await navigator.share({ title: content.title, text: content.text, url: content.url })
            return { status: "ok", value: undefined }
        } catch (error) {
            return failure(error)
        }
    }

    async exportTextFile(file: TextFileExport): Promise<PlatformResult<void>> {
        try {
            const url = URL.createObjectURL(new Blob([file.data], { type: file.mediaType }))
            const link = document.createElement("a")
            link.href = url
            link.download = exportFilename(file.filename)
            document.body.append(link)
            link.click()
            link.remove()
            URL.revokeObjectURL(url)
            return { status: "ok", value: undefined }
        } catch (error) {
            return failure(error)
        }
    }

    async writeClipboard(text: string): Promise<PlatformResult<void>> {
        if (!navigator.clipboard?.writeText) return { status: "unavailable", reason: "Clipboard access is unavailable" }
        try {
            await navigator.clipboard.writeText(text)
            return { status: "ok", value: undefined }
        } catch (error) {
            return failure(error)
        }
    }
}

class NativePlatformAdapter implements PlatformAdapter {
    readonly nestClientPlatform = "mobile" as const
    readonly capabilities = nativeCapabilities

    constructor(readonly kind: NativeMobilePlatform) {}

    async getInfo(): Promise<PlatformInfo> {
        try {
            const info = await App.getInfo()
            return { kind: this.kind, native: true, appVersion: info.version, appBuild: info.build }
        } catch {
            return { kind: this.kind, native: true }
        }
    }

    async getLifecycleState(): Promise<AppLifecycleState> {
        return (await App.getState()).isActive ? "active" : "background"
    }

    async addLifecycleListener(listener: (state: AppLifecycleState) => void): Promise<PlatformListener> {
        const handle = await App.addListener("appStateChange", ({ isActive }) => listener(isActive ? "active" : "background"))
        return { remove: () => handle.remove() }
    }

    async getLaunchUrl(): Promise<string | null> {
        const launch = await App.getLaunchUrl()
        return launch?.url ?? null
    }

    async addUrlOpenListener(listener: (url: string) => void): Promise<PlatformListener> {
        const handle = await App.addListener("appUrlOpen", ({ url }) => listener(url))
        return { remove: () => handle.remove() }
    }

    async getNotificationPermission(): Promise<PlatformResult<NotificationPermissionState>> {
        try {
            const permission = await PushNotifications.checkPermissions()
            return { status: "ok", value: normalizeNotificationPermission(permission.receive) }
        } catch (error) {
            return failure(error)
        }
    }

    async requestNotificationPermission(): Promise<PlatformResult<NotificationPermissionState>> {
        try {
            const permission = await PushNotifications.requestPermissions()
            const state = normalizeNotificationPermission(permission.receive)
            return state === "denied" ? { status: "denied", reason: "Notification permission was denied" } : { status: "ok", value: state }
        } catch (error) {
            return failure(error)
        }
    }

    async addPushRegistrationListener(listener: (event: PushRegistrationEvent) => void): Promise<PlatformListener> {
        const [registration, registrationError] = await Promise.all([
            PushNotifications.addListener("registration", ({ value }) => {
                if (value.trim().length > 0) listener({ type: "registered", transport: this.kind === "ios" ? "apns" : "fcm", token: value })
                else listener({ type: "failed", message: "Native push registration returned an empty token" })
            }),
            PushNotifications.addListener("registrationError", ({ error }) => listener({ type: "failed", message: error })),
        ])
        return {
            remove: async () => {
                await Promise.all([registration.remove(), registrationError.remove()])
            },
        }
    }

    async registerForPushNotifications(): Promise<PlatformResult<void>> {
        try {
            await PushNotifications.register()
            return { status: "ok", value: undefined }
        } catch (error) {
            return failure(error)
        }
    }

    async addNotificationTapListener(listener: (tap: NotificationTap) => void): Promise<PlatformListener> {
        const handle = await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
            try {
                listener(parseNotificationTap(action))
            } catch {
                console.warn("[mobile-notifications] Ignored a push tap with an invalid route")
            }
        })
        return { remove: () => handle.remove() }
    }

    async addForegroundNotificationListener(listener: (notification: ForegroundNotification) => void): Promise<PlatformListener> {
        const handle = await PushNotifications.addListener("pushNotificationReceived", (notification) => {
            try {
                listener(parseForegroundNotification(notification))
            } catch {
                // Live-status-only and malformed data messages do not produce a foreground alert or haptic.
            }
        })
        return { remove: () => handle.remove() }
    }

    async addBackButtonListener(listener: (event: BackButtonPress) => void): Promise<PlatformListener> {
        if (this.kind !== "android") return { remove: async () => undefined }
        const handle = await App.addListener("backButton", listener)
        return { remove: () => handle.remove() }
    }

    async minimizeApp(): Promise<PlatformResult<void>> {
        if (this.kind !== "android") return { status: "unavailable", reason: "App minimization is available only on Android" }
        try {
            await App.minimizeApp()
            return { status: "ok", value: undefined }
        } catch (error) {
            return failure(error)
        }
    }

    async setStatusBarTheme(theme: StatusBarTheme): Promise<PlatformResult<void>> {
        try {
            await StatusBar.setBackgroundColor({ color: theme.backgroundColor })
            await StatusBar.setStyle({ style: theme.foreground === "light" ? StatusBarStyle.Dark : StatusBarStyle.Light })
            return { status: "ok", value: undefined }
        } catch (error) {
            return failure(error)
        }
    }

    async haptic(kind: HapticKind): Promise<PlatformResult<void>> {
        try {
            if (kind === "selection") await Haptics.selectionChanged()
            else if (kind === "light") await Haptics.impact({ style: ImpactStyle.Light })
            else if (kind === "medium") await Haptics.impact({ style: ImpactStyle.Medium })
            else {
                const type = kind === "success" ? NotificationType.Success : kind === "warning" ? NotificationType.Warning : NotificationType.Error
                await Haptics.notification({ type })
            }
            return { status: "ok", value: undefined }
        } catch (error) {
            return failure(error)
        }
    }

    async openExternalUrl(url: string): Promise<PlatformResult<void>> {
        try {
            const result = await AppLauncher.openUrl({ url: normalizeExternalUrl(url) })
            return result.completed ? { status: "ok", value: undefined } : { status: "denied", reason: "No application accepted this URL" }
        } catch (error) {
            return failure(error)
        }
    }

    async share(content: ShareContent): Promise<PlatformResult<void>> {
        try {
            await Share.share(content)
            return { status: "ok", value: undefined }
        } catch (error) {
            return failure(error)
        }
    }

    async exportTextFile(file: TextFileExport): Promise<PlatformResult<void>> {
        let path: string | null = null
        try {
            const filename = exportFilename(file.filename)
            path = `wildlark-exports/${Date.now()}-${filename}`
            await Filesystem.writeFile({
                path,
                data: file.data,
                directory: Directory.Cache,
                encoding: Encoding.UTF8,
                recursive: true,
            })
            const uri = await Filesystem.getUri({ path, directory: Directory.Cache })
            await Share.share({
                title: file.title ?? filename,
                files: [uri.uri],
                dialogTitle: "Share export",
            })
            return { status: "ok", value: undefined }
        } catch (error) {
            return failure(error)
        } finally {
            if (path) void Filesystem.deleteFile({ path, directory: Directory.Cache }).catch(() => undefined)
        }
    }

    async writeClipboard(text: string): Promise<PlatformResult<void>> {
        try {
            await Clipboard.write({ string: text })
            return { status: "ok", value: undefined }
        } catch (error) {
            return failure(error)
        }
    }
}

let cachedPlatformAdapter: PlatformAdapter | null = null

export function getPlatformAdapter(): PlatformAdapter {
    if (cachedPlatformAdapter) return cachedPlatformAdapter
    const kind = currentPlatformKind()
    cachedPlatformAdapter = kind === "ios" || kind === "android" ? new NativePlatformAdapter(kind) : new WebPlatformAdapter(kind)
    return cachedPlatformAdapter
}

export function resetPlatformAdapterForTests(): void {
    cachedPlatformAdapter = null
}
